import { computed, type ComputedRef } from "vue";
import { useDisplay } from "vuetify";
import { useRoute } from "vue-router";
import useStore from "@/stores";
import GlobalService from "@/services/global-service";
import Unit from "@/types/clients/esquire/data/general/contract-data/unit";

export type NavItem = {
  route: string;
  title: string;
  icon: string;
  show: boolean;
};

export type AppNav = {
  isMobile: ComputedRef<boolean>;
  isGuest: ComputedRef<boolean>;
  showBottomNav: ComputedRef<boolean>;
  showTopNavItems: ComputedRef<boolean>;
  routeName: ComputedRef<string>;
  visibleItems: ComputedRef<NavItem[]>;
};

export function useAppNav(): AppNav {
  const { session } = useStore();
  const display = useDisplay();
  const route = useRoute();


  const contract = computed(() => session.contract);
  const isActive = computed<boolean>(() => session.contract?.contractStatus === "active");

  const hasDomotics = computed<boolean>(() => {
    const unit = contract.value?.unit as Unit | undefined;
    return !!unit?.domoticsDevices?.length;
  });

  const isGuest = computed<boolean>(() => session.isGuest);
  const isMobile = computed<boolean>(() => display.smAndDown.value);

  const showBottomNav = computed<boolean>(() => isMobile.value && !session.isAdmin);
  const showTopNavItems = computed<boolean>(() => !isMobile.value && !session.isAdmin);

  const guestItems = computed<NavItem[]>(() => [
    {
      route: "home",
      title: GlobalService.translator().trans("general.menu.home"),
      icon: "mdi-home-outline",
      show: true
    },
    {
      route: "comfort",
      title: GlobalService.translator().trans("general.breadcrumbs.comfort"),
      icon: "mdi-home-thermometer-outline",
      show: isActive.value && hasDomotics.value
    },
    {
      route: "services",
      title: GlobalService.translator().trans("general.breadcrumbs.services"),
      icon: "mdi-room-service-outline",
      show: true
    },
    {
      route: "profile",
      title: GlobalService.translator().trans("general.breadcrumbs.profile"),
      icon: "mdi-account-outline",
      show: true
    }
  ]);

  const publicItems = computed<NavItem[]>(() => [
    {
      route: "auth",
      title: GlobalService.translator().trans("general.auth.login"),
      icon: "mdi-login",
      show: true
    },
    {
      route: "services",
      title: GlobalService.translator().trans("general.breadcrumbs.services"),
      icon: "mdi-room-service-outline",
      show: true
    }
  ]);

  const items = computed<NavItem[]>(() => {
    return session.isLoggedIn ? guestItems.value : publicItems.value;
  });

  const visibleItems = computed<NavItem[]>(() => items.value.filter((item: NavItem): boolean => item.show));
  const routeName = computed<string>(() => String(route.name ?? ""));

  return {
    isMobile,
    isGuest,
    showBottomNav,
    showTopNavItems,
    routeName,
    visibleItems
  };
}
